import { useEffect, useRef, useState } from 'react';
import { authService } from '../../services/auth';
import { i18nService } from '../../services/i18n';
import { formatCredit, type CreditRequests, type CreditSelf } from '../../services/credit';

/** 会话积分快照仅在当前账号、当前会话有效，不持久化登录信息。 */
export interface SessionCreditSnapshot {
  sessionId: string;
  account: CreditSelf | null;
  usage: CreditRequests | null;
  stale: boolean;
}

/** 会话打开、终态变化和页面聚焦时刷新；结束后有限重试等待平台入账。 */
export function useSessionCredit(sessionId: string | undefined, status: string | undefined): SessionCreditSnapshot | null {
  const [snapshot, setSnapshot] = useState<SessionCreditSnapshot | null>(null);
  const generation = useRef(0);
  useEffect(() => {
    const current = ++generation.current;
    let request = 0;
    let alive = true;
    // 终态切换只触发刷新，不能先丢弃同会话已确认金额；账号变化仍由下面的订阅清空。
    setSnapshot(old => old?.sessionId === sessionId ? old : null);
    if (!sessionId) return;
    /** 每次刷新校验两个接口模式一致；旧请求、切账号响应不能更新页面。 */
    const refresh = async () => {
      const seq = ++request;
      const [account, usage] = await Promise.all([authService.credits.self(), authService.credits.requests(sessionId)]);
      if (!alive || current !== generation.current || seq !== request) return;
      if (account.status === 'ready' && usage.status === 'ready' && account.data.mode === usage.data.mode) {
        setSnapshot({ sessionId, account: account.data, usage: usage.data, stale: false });
      } else {
        const transient = [account.status, usage.status].every(s => s === 'ready' || s === 'unavailable');
        setSnapshot(old => transient && old?.sessionId === sessionId
          ? { ...old, stale: true } : { sessionId, account: null, usage: null, stale: true });
      }
    };
    void refresh();
    const timers = status !== 'running' ? [2000, 5000, 15000].map(ms => window.setTimeout(refresh, ms)) : [];
    const onFocus = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', onFocus);
    const unsubscribe = authService.subscribe(() => { request++; setSnapshot(null); void refresh(); });
    return () => { alive = false; request++; timers.forEach(window.clearTimeout); window.removeEventListener('focus', onFocus); unsubscribe(); };
  }, [sessionId, status]);
  return snapshot?.sessionId === sessionId ? snapshot : null;
}

/** 读取已入账费用；轮次缺少映射或账单时返回 null，不能把未确认费用当成零。 */
export function sessionCreditAmount(snapshot: SessionCreditSnapshot | null, turnIds?: unknown): number | null {
  const usage = snapshot?.usage;
  const ids = Array.isArray(turnIds) ? [...new Set(turnIds.filter((id): id is string => typeof id === 'string' && !!id))] : [];
  const perTurn = turnIds !== undefined;
  let amount: number | null = null;
  if (usage) {
    if (!perTurn) amount = usage.total_requests > 0 ? usage.total_mcr : null;
    else if (ids.length && usage.turns) {
      const rows = ids.map(id => usage.turns?.find(row => row.turn_id === id));
      if (rows.every(row => row && row.requests > 0)) {
        amount = rows.reduce((sum, row) => sum + (usage.mode === 'shadow' ? row!.credit_mcr_shadow : row!.credit_mcr), 0);
      }
    }
  }
  return amount;
}

/** 成功响应缺少 turns 表示平台未提供分轮能力；失败或过期快照不能用于判断能力。 */
function lacksTurnBreakdown(snapshot: SessionCreditSnapshot | null): boolean {
  return !!snapshot?.usage && !snapshot.stale && snapshot.usage.turns === null;
}

/** 从平台全量轮次汇总读取费用；缺失映射/日志时不能显示免费。 */
export function SessionCreditLabel({ snapshot, turnIds, label }: {
  snapshot: SessionCreditSnapshot | null;
  turnIds?: unknown;
  /** 同一计费语义在顶部与轮次底栏使用不同展示名称。 */
  label?: string;
}) {
  const t = (key: string) => i18nService.t(`credit${key}`);
  if (snapshot?.account?.mode === 'off') return null;
  const usage = snapshot?.usage;
  const perTurn = turnIds !== undefined;
  const amount = sessionCreditAmount(snapshot, turnIds);
  const unavailable = perTurn && lacksTurnBreakdown(snapshot);
  const value = amount === null ? t(unavailable ? 'TurnBreakdownUnavailable' : 'PendingSettlement') : `${formatCredit(amount, snapshot?.account?.credit_per_quota ?? 0)} ${t('Unit')}`;
  return <span className="text-[11px] dark:text-claude-darkTextSecondary text-claude-textSecondary" title={t(unavailable ? 'TurnBreakdownUnavailableHint' : 'RecordedUsageHint')}>
    {label ?? t(perTurn ? 'TurnUsage' : 'SessionUsage')}: {value}
    {usage?.mode === 'shadow' && ` · ${t('ShadowShort')}`}
    {snapshot?.stale && ` · ${t('StaleShort')}`}
  </span>;
}

/** 顶部展示最近轮次与会话累计；区分等待入账和平台缺少分轮能力，不借用上一轮费用。 */
export function SessionCreditOverview({ snapshot, latestTurnIds, running }: {
  snapshot: SessionCreditSnapshot | null;
  /** 最新主用户轮次的持久计费 ID；缺失时传空列表，不按时间推测关联。 */
  latestTurnIds: unknown;
  /** 运行中费用可能仍在入账，此处不显示临时金额为终值。 */
  running: boolean;
}) {
  if (snapshot?.account?.mode === 'off') return null;
  const shadow = snapshot?.usage?.mode === 'shadow';
  const t = (key: string) => i18nService.t(`credit${key}`);
  const renderAmount = (amount: number | null) => amount === null
    ? t('PendingSettlement') : `${formatCredit(amount, snapshot?.account?.credit_per_quota ?? 0)} ${t('Unit')}`;
  const unavailable = !running && lacksTurnBreakdown(snapshot);
  const turn = unavailable ? t('TurnBreakdownUnavailable') : renderAmount(sessionCreditAmount(snapshot, running ? [] : latestTurnIds ?? []));
  const total = renderAmount(sessionCreditAmount(snapshot));
  const text = `${t(shadow ? 'TurnSimulated' : 'TurnConsumed')} ${turn} · ${t(shadow ? 'SessionSimulated' : 'SessionTotal')} ${total}`;
  return <div
    className="min-w-0 truncate rounded-full border border-claude-border px-2 py-0.5 font-mono text-[11px] leading-4 whitespace-nowrap text-claude-textSecondary dark:border-claude-darkBorder dark:text-claude-darkTextSecondary"
    data-session-credit-overview
    title={`${text} · ${t(unavailable ? 'TurnBreakdownUnavailableHint' : 'RecordedUsageHint')}${snapshot?.stale ? ` · ${t('StaleShort')}` : ''}`}
  >
    {text}{snapshot?.stale && ` · ${t('StaleShort')}`}
  </div>;
}
